/**
 * Easter doodles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterDoodlesPack: ToilePack = {
    slug: 'easter-doodles',
    category: 'season',
    name: 'Easter doodles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'mixed', label: 'Mixed doodles', subject: 'Small easter doodles packed edge to edge: an egg, a carrot, a tulip, a chick, a basket, a bee, a bunny, no gaps'},
        {key: 'eggs', label: 'Egg doodles', subject: 'Small eggs each with a different pattern, packed edge to edge, no gaps'},
        {key: 'garden', label: 'Garden doodles', subject: 'Small spring garden doodles packed edge to edge: a watering can, seedlings, a snail, a butterfly, a daisy, no gaps'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['add a lamb', 'in pastel line weight', 'for a planner sticker sheet'],
    directionHint: 'try: add a lamb',
    seo: {
        title: 'Easter doodles: mixed, egg, garden',
        metaDescription: 'Nine Easter doodle sheets — mixed, egg, garden — packed edge to edge in simple, detailed or doodle line art, ready to color or cut apart.',
        h1: 'Easter doodles: mixed, egg and garden sheets',
        tagline: 'Nine doodle sheets, three Easter themes, three line styles each — packed edge to edge, ready for the journal or sticker sheet.',
        intro: 'A doodle sheet packed edge to edge for spring: an egg, a carrot, a tulip, a chick, a bee, no gaps. These nine run three themes — a mixed page, an all-egg page of patterns, and a garden page of watering cans and snails — in bold simple lines, intricate detailed line art, or loose doodle strokes. Color the page or cut it into stickers.',
        useCases: [
            'a sticker-making craft session',
            'a classroom Easter filler activity',
            'a journal or planner decoration source',
            'an Easter-table coloring activity',
        ],
        faq: [
            {q: 'What size do the sheets print at?', a: 'Each sheet is drawn square with a white margin, so it prints clean on standard paper.'},
            {q: 'Can a sheet be cut apart into stickers?', a: 'Yes — every doodle is a clean closed outline, loosely spaced enough to cut apart by hand.'},
            {q: 'Can I get a different Easter theme?', a: "Yes — that's what the direction field is for. Type a different theme, like a lamb, and the sheet redraws in the same three line styles."},
        ],
    },
}
